import type { ResolvedPhase } from 'modules/Cube/domain/cube.domain.types'
import findLastIndex from 'lodash/fp/findLastIndex'
import { isAfter, isBefore } from '@sequencehq/utils/dates'
import type { PhaseExpansion } from './phaseEditor.atoms'

/**
 * Expands the last active phase, or the first phase when they are all in the
 * future.
 */
export const getDefaultActivePhaseIndex = (
  sortedPhases: Pick<ResolvedPhase, 'dates'>[],
  dateToCompare: Date = new Date()
): number => {
  const activePhaseIndex = findLastIndex(
    (phase: Pick<ResolvedPhase, 'dates'>) => {
      if (!phase.dates.absolute.start) {
        return false
      }

      if (!isAfter(dateToCompare, phase.dates.absolute.start)) {
        return false
      }

      // A phase without an end date stays active after its start date.
      return (
        !phase.dates.absolute.end ||
        isBefore(dateToCompare, phase.dates.absolute.end)
      )
    }
  )(sortedPhases)

  return activePhaseIndex === -1 ? 0 : activePhaseIndex
}

/** Amendment quotes open their editable phase; other quotes open today's phase. */
export const getIsPhaseExpandedByDefault = ({
  phase,
  idx,
  sortedPhases,
  amendmentPhaseId
}: {
  phase: Pick<ResolvedPhase, 'id'>
  idx: number
  sortedPhases: Pick<ResolvedPhase, 'dates'>[]
  amendmentPhaseId: ResolvedPhase['id'] | undefined
}): boolean =>
  amendmentPhaseId
    ? phase.id === amendmentPhaseId
    : getDefaultActivePhaseIndex(sortedPhases) === idx

export const getExistingPhaseExpansion = ({
  phaseExpansion,
  phaseId,
  idx
}: {
  phaseExpansion: PhaseExpansion[]
  phaseId: ResolvedPhase['id']
  idx: number
}) =>
  phaseExpansion.find(previousPhase => previousPhase.id === phaseId) ??
  phaseExpansion[idx]

export const getIsPhaseHighlighted = (
  phase: ResolvedPhase,
  highlightedEntityIds: string[]
) => {
  const entityIdsForPhase = [
    phase.id,
    ...phase.prices.map(price => price.id),
    ...phase.discounts.map(discount => discount.id),
    ...phase.minimums.map(minimum => minimum.id)
  ]

  return highlightedEntityIds.some(id => entityIdsForPhase.includes(id))
}

export const getInitialPhaseExpansion = ({
  sortedPhases,
  amendmentPhaseId,
  highlightedEntityIds
}: {
  sortedPhases: ResolvedPhase[]
  amendmentPhaseId: ResolvedPhase['id'] | undefined
  highlightedEntityIds: string[]
}) =>
  sortedPhases.map((phase, idx) => ({
    id: phase.id,
    expanded:
      getIsPhaseExpandedByDefault({
        phase,
        idx,
        sortedPhases,
        amendmentPhaseId
      }) || getIsPhaseHighlighted(phase, highlightedEntityIds)
  }))
